import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, DotsSixVertical, Flag, Lock } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import UserAvatar from "@/components/UserAvatar";

const STATUS_TOOLTIPS = {
  todo: "Committed tasks with dates assigned, ready to be picked up next.",
  in_progress: "Tasks actively being worked on by a team member.",
  review: "Completed work awaiting review or sign-off before it can be marked done.",
  done: "Tasks that are fully completed, reviewed, and accepted.",
};

const columns = [
  { id: "todo", label: "To Do", color: "#0055FF" },
  { id: "in_progress", label: "In Progress", color: "#D97706" },
  { id: "review", label: "Review", color: "#7C3AED" },
  { id: "done", label: "Done", color: "#16A34A" },
];

const priorityStyles = {
  high: "bg-[#FEF2F2] text-[#DC2626]",
  medium: "bg-[#FFFBEB] text-[#D97706]",
  low: "bg-[#F5F5F5] text-[#A3A3A3]",
};

const PRIORITY_CYCLE = ["high", "medium", "low"];
const STATUS_CYCLE = ["todo", "in_progress", "review", "done"];
const nextInCycle = (arr, v) => arr[(arr.indexOf(v) + 1) % arr.length];

export default function KanbanBoard({ tasks, projectId, user, onTaskUpdate, onTaskClick }) {
  const isClient = user?.user_type === "client";
  // Session 69 — clients get a stripped-down task creation form
  // (title + description + priority only). Start date defaults to
  // TODAY and is fixed; assignee, status, duration, end-date are all
  // hidden. The internal team picks those fields up after the task
  // posts (and gets a notification — see backend `routes/tasks.py`).
  const todayISO = new Date().toISOString().split("T")[0];
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "", description: "", priority: "medium",
    status: "todo", assignee_id: "",
    start_date: isClient ? todayISO : "",
    end_date: "", duration_days: "", is_showstopper: false,
    visibility: "public",
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [optimistic, setOptimistic] = useState({}); // { taskId: { priority?, status? } }

  // Merge optimistic updates over incoming tasks so the UI reflects clicks instantly
  const displayTasks = tasks.map(t => optimistic[t.id] ? { ...t, ...optimistic[t.id] } : t);

  useEffect(() => {
    api.get("/users").then(res => setTeamMembers(res.data)).catch(() => {});
  }, []);

  // Clear optimistic overrides once the server-side data catches up
  useEffect(() => {
    setOptimistic(prev => {
      let changed = false;
      const next = { ...prev };
      for (const t of tasks) {
        const o = next[t.id];
        if (!o) continue;
        const stillDiffers = Object.entries(o).some(([k, v]) => t[k] !== v);
        if (!stillDiffers) { delete next[t.id]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [tasks]);

  const handleDragStart = (e, taskId) => {
    setDragId(taskId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCol(colId);
  };

  const handleDragLeave = () => {
    setDragOverCol(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!dragId) return;

    const task = tasks.find(t => t.id === dragId);
    if (!task || task.status === newStatus) {
      setDragId(null);
      return;
    }

    try {
      await api.put(`/tasks/${dragId}`, { status: newStatus });
      onTaskUpdate?.();
      toast.success(`Moved to ${columns.find(c => c.id === newStatus)?.label}`);
    } catch (err) {
      toast.error("Failed to update task");
    }
    setDragId(null);
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) return toast.error("Title is required");
    try {
      const payload = { ...newTask };
      if (!payload.assignee_id || payload.assignee_id === "unassigned") delete payload.assignee_id;
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      if (payload.duration_days === "" || payload.duration_days === null) delete payload.duration_days;
      await api.post(`/projects/${projectId}/tasks`, payload);
      toast.success("Task created");
      setShowCreate(false);
      setNewTask({ title: "", description: "", priority: "medium", status: "todo", assignee_id: "", start_date: "", end_date: "", duration_days: "", is_showstopper: false, visibility: "public" });
      onTaskUpdate?.();
    } catch (err) {
      toast.error("Failed to create task");
    }
  };

  const cyclePriority = async (e, task) => {
    e.stopPropagation();
    const next = nextInCycle(PRIORITY_CYCLE, task.priority || "medium");
    // Immediate UI feedback
    setOptimistic(o => ({ ...o, [task.id]: { ...(o[task.id] || {}), priority: next } }));
    try {
      await api.put(`/tasks/${task.id}`, { priority: next });
      onTaskUpdate?.();
    } catch (err) {
      // Revert on failure
      setOptimistic(o => { const n = { ...o }; delete n[task.id]; return n; });
      toast.error("Failed to update priority");
    }
  };

  return (
    <div data-testid="kanban-board">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        {(user?.user_type === "internal" || user?.role === "client_admin") && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-9 px-4 text-xs" data-testid="create-task-button">
                <Plus size={14} weight="bold" className="mr-1.5" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>New Task</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Title</Label>
                  <Input value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" placeholder="Task title" data-testid="task-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Description</Label>
                  <Textarea value={newTask.description} onChange={e => setNewTask({...newTask, description: e.target.value})} className="rounded-none border-[#E5E5E5] min-h-[60px]" placeholder="Optional description" data-testid="task-description-input" />
                </div>
                <div className={isClient ? "" : "grid grid-cols-2 gap-3"}>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Priority</Label>
                    <Select value={newTask.priority} onValueChange={v => setNewTask({...newTask, priority: v})}>
                      <SelectTrigger className="rounded-none border-[#E5E5E5] h-10" data-testid="task-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {!isClient && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Assignee</Label>
                    <Select value={newTask.assignee_id} onValueChange={v => setNewTask({...newTask, assignee_id: v})}>
                      <SelectTrigger className="rounded-none border-[#E5E5E5] h-10" data-testid="task-assignee-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {teamMembers.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                </div>
                {!isClient && (
                <div className="grid grid-cols-[5fr_2fr_5fr] gap-3 items-end">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Start Date</Label>
                    <Input type="date" value={newTask.start_date} onChange={e => {
                      const sd = e.target.value;
                      const updates = { start_date: sd };
                      if (sd && newTask.duration_days) {
                        const d = new Date(sd);
                        d.setDate(d.getDate() + parseInt(newTask.duration_days));
                        updates.end_date = d.toISOString().split('T')[0];
                      } else if (sd && !newTask.duration_days) {
                        const d = new Date(sd);
                        d.setDate(d.getDate() + 7);
                        updates.end_date = d.toISOString().split('T')[0];
                        updates.duration_days = 7;
                      }
                      setNewTask(prev => ({...prev, ...updates}));
                    }} className="rounded-none border-[#E5E5E5] h-10" data-testid="task-start-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Duration (days)</Label>
                    <Input type="number" min={1} value={newTask.duration_days || ""} onChange={e => {
                      const raw = e.target.value;
                      const dur = raw === "" ? "" : parseInt(raw);
                      const updates = { duration_days: dur };
                      if (newTask.start_date && dur > 0) {
                        const d = new Date(newTask.start_date);
                        d.setDate(d.getDate() + dur);
                        updates.end_date = d.toISOString().split('T')[0];
                      }
                      setNewTask(prev => ({...prev, ...updates}));
                    }} className="rounded-none border-[#E5E5E5] h-10 min-w-0" placeholder="7" data-testid="task-duration-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">End Date</Label>
                    <Input type="date" value={newTask.end_date} onChange={e => {
                      const ed = e.target.value;
                      const updates = { end_date: ed };
                      if (newTask.start_date && ed) {
                        const sd = new Date(newTask.start_date);
                        const endD = new Date(ed);
                        const diff = Math.round((endD - sd) / (1000 * 60 * 60 * 24));
                        updates.duration_days = diff > 0 ? diff : 0;
                      }
                      setNewTask(prev => ({...prev, ...updates}));
                    }} className="rounded-none border-[#E5E5E5] h-10 min-w-0" data-testid="task-end-date" />
                  </div>
                </div>
                )}
                {!isClient && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Status</Label>
                  <Select value={newTask.status} onValueChange={v => setNewTask({...newTask, status: v})}>
                    <SelectTrigger className="rounded-none border-[#E5E5E5] h-10" data-testid="task-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {columns.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                )}
                {isClient && (
                  <div className="text-[11px] text-[#737373] bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-2">
                    Start date will be set to today ({todayISO}). Our team will assign the
                    task, set timing, and follow up after you submit.
                  </div>
                )}
                {!isClient && (
                  <label
                    className={`flex items-start gap-2.5 border px-3 py-2.5 cursor-pointer transition-colors ${
                      newTask.is_showstopper
                        ? "bg-[#FEE2E2] border-[#FCA5A5]"
                        : "bg-white border-[#E5E5E5] hover:border-[#A3A3A3]"
                    }`}
                    data-testid="task-showstopper-label"
                  >
                    <input
                      type="checkbox"
                      checked={!!newTask.is_showstopper}
                      onChange={(e) => setNewTask({ ...newTask, is_showstopper: e.target.checked })}
                      className="mt-0.5 accent-[#DC2626]"
                      data-testid="task-showstopper-checkbox"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Flag size={12} weight={newTask.is_showstopper ? "fill" : "regular"} className={newTask.is_showstopper ? "text-[#DC2626]" : "text-[#525252]"} />
                        <span className={`text-xs font-bold uppercase tracking-[0.15em] ${newTask.is_showstopper ? "text-[#991B1B]" : "text-[#525252]"}`}>
                          Showstopper
                        </span>
                      </div>
                      <p className="text-[11px] text-[#737373] mt-1 leading-snug">
                        Flag if this task is blocking the project's go-live. Surfaces at the top of the client's daily digest in red.
                      </p>
                    </div>
                  </label>
                )}
                {!isClient && (
                  <label
                    className={`flex items-start gap-2.5 border px-3 py-2.5 cursor-pointer transition-colors ${
                      newTask.visibility === "private"
                        ? "bg-[#F5F3FF] border-[#C4B5FD]"
                        : "bg-white border-[#E5E5E5] hover:border-[#A3A3A3]"
                    }`}
                    data-testid="task-visibility-label"
                  >
                    <input
                      type="checkbox"
                      checked={newTask.visibility === "private"}
                      onChange={(e) => setNewTask({ ...newTask, visibility: e.target.checked ? "private" : "public" })}
                      className="mt-0.5 accent-[#6D28D9]"
                      data-testid="task-visibility-checkbox"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Lock size={12} weight={newTask.visibility === "private" ? "fill" : "regular"} className={newTask.visibility === "private" ? "text-[#6D28D9]" : "text-[#525252]"} />
                        <span className={`text-xs font-bold uppercase tracking-[0.15em] ${newTask.visibility === "private" ? "text-[#5B21B6]" : "text-[#525252]"}`}>
                          Private — internal team only
                        </span>
                      </div>
                      <p className="text-[11px] text-[#737373] mt-1 leading-snug">
                        Hide this task from the client. Only Eyvo internal users will see it on the Kanban, list, Gantt, and project digest. Cannot be assigned to a client user.
                      </p>
                    </div>
                  </label>
                )}
                <Button onClick={handleCreateTask} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="submit-task-button">
                  Create Task
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
          {tasks.length} Tasks
        </h3>
      </div>

      {/* Columns */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => {
          // v1.70 — sort newest-first per Mike's Jun-17 request.
          // ISO timestamps sort lexicographically same as chronologically,
          // so a desc string compare on `created_at` is correct. Tasks
          // with no created_at (shouldn't happen) sink to the bottom.
          const colTasks = displayTasks
            .filter(t => t.status === col.id)
            .sort((a, b) => {
              const va = a.created_at || "";
              const vb = b.created_at || "";
              return vb.localeCompare(va);
            });
          return (
            <div
              key={col.id}
              className={`kanban-column flex-shrink-0 w-[260px] border border-[#E5E5E5] bg-[#FAFAFA] p-3 ${
                dragOverCol === col.id ? "drag-over !bg-[#F0F7FF] !border-[#0055FF]" : ""
              }`}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col.id)}
              data-testid={`kanban-column-${col.id}`}
            >
              {/* Column Header */}
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#525252] cursor-help border-b border-dashed border-[#D4D4D4]">{col.label}</span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[220px] text-xs bg-[#0A0A0A] text-white rounded-none px-3 py-2">
                      <p>{STATUS_TOOLTIPS[col.id]}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="text-xs text-[#A3A3A3] ml-auto">{colTasks.length}</span>
              </div>

              {/* Cards */}
              <div className="space-y-2 min-h-[100px]">
                {colTasks.map((task) => (
                  <div
                    key={task.id}
                    draggable={user?.user_type === "internal"}
                    onDragStart={(e) => handleDragStart(e, task.id)}
                    onClick={() => onTaskClick?.(task)}
                    className={`kanban-card border p-3 ${
                      task.is_showstopper
                        ? "bg-[#FEE2E2] border-[#DC2626] border-l-4"
                        : task.ball_with === "Client"
                        ? "bg-[#FEE2E2] border-[#FCA5A5]"
                        : "bg-white border-[#E5E5E5]"
                    } ${dragId === task.id ? "opacity-50" : ""}`}
                    data-testid={`kanban-card-${task.id}`}
                    data-ball-with={task.ball_with || "Eyvo"}
                    data-showstopper={task.is_showstopper ? "1" : "0"}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        {task.is_showstopper && (
                          <Badge
                            className="bg-[#DC2626] text-white text-[9px] font-bold uppercase px-1.5 py-0 border-0 rounded-none mr-1.5 inline-flex items-center gap-1"
                            data-testid={`kanban-showstopper-${task.id}`}
                            title="Showstopper — blocking go-live"
                          >
                            <Flag size={9} weight="fill" /> SHOWSTOPPER
                          </Badge>
                        )}
                        {task.visibility === "private" && (
                          <Badge
                            className="bg-[#6D28D9] text-white text-[9px] font-bold uppercase px-1.5 py-0 border-0 rounded-none mr-1.5 inline-flex items-center gap-1"
                            data-testid={`kanban-private-${task.id}`}
                            title="Private — hidden from the client"
                          >
                            <Lock size={9} weight="fill" /> PRIVATE
                          </Badge>
                        )}
                        {task.task_number && <span className="text-[10px] font-bold text-[#0055FF] mr-1">{task.task_number}</span>}
                        <h4 className="text-sm font-medium text-[#0A0A0A] leading-snug">{task.title}</h4>
                      </div>
                      {user?.user_type === "internal" && (
                        <DotsSixVertical size={14} className="text-[#A3A3A3] flex-shrink-0 ml-2 cursor-grab" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        onClick={user?.user_type === "internal" ? (e) => cyclePriority(e, task) : undefined}
                        title={user?.user_type === "internal" ? "Click to cycle priority" : undefined}
                        className={`${priorityStyles[task.priority] || priorityStyles.medium} text-[10px] font-bold uppercase px-1.5 py-0 border-0 rounded-none ${user?.user_type === "internal" ? "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-[#0055FF]" : ""}`}
                        data-testid={`kanban-priority-${task.id}`}
                      >
                        {task.priority}
                      </Badge>
                      {task.assignee_name && (
                        <span className="flex items-center gap-1.5" data-testid={`kanban-assignee-${task.id}`}>
                          <UserAvatar
                            name={task.assignee_name}
                            avatar={task.assignee_avatar}
                            size="xs"
                            testId={`kanban-assignee-avatar-${task.id}`}
                          />
                          <span className="text-[10px] text-[#A3A3A3] truncate">{task.assignee_name}</span>
                        </span>
                      )}
                    </div>
                    {task.progress > 0 && task.progress < 100 && (
                      <div className="mt-2 w-full h-1 bg-[#F5F5F5]">
                        <div className="h-full bg-[#0055FF]" style={{ width: `${task.progress}%` }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
